import React, { useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { useLocation, useHistory } from 'react-router-dom';
import { parseQueryParams } from 'utilities/query-params-util';

import HomeIcon from '@material-ui/icons/Home';
import UndoIcon from '@material-ui/icons/Undo';
import ContactMailIcon from '@material-ui/icons/ContactMail';

import useRouteAction from 'hooks/route-action/use-route-action';

import HeaderImage from '../../../assets/oops.png';
import useUserStore from 'store/user';
import config from 'config';

const useStyles = makeStyles((theme: Theme) => {
  return {
    card: {
      width: 600,
      padding: theme.spacing(3)
    },
    imageContainer: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1)
    },
    title: {
      marginBottom: theme.spacing()
    },
    titleContainer: {
      textAlign: 'center',
      marginBottom: theme.spacing(3)
    },
    errorMessage: {
      marginBottom: theme.spacing()
    },
    homeButton: {
      marginLeft: theme.spacing(2)
    },
    contactButton: {
      width: 150
    },
    contactButtonContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    dividerSpacing: {
      width: '100%',
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3)
    }
  };
});

const ErrorPage = (): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('ErrorPage');
  const location = useLocation();
  const history = useHistory();

  const { returnUrl } = parseQueryParams(location.search) || {};
  const [, { refetchUser }] = useUserStore();

  const handleClick = useCallback(
    (path: string) => (): void => {
      history.replace(path);
      refetchUser();
    },
    [refetchUser, history]
  );

  const handleContactClick = (): Window | null =>
    window.open(config.digitalProductSupportUrl, '_blank');

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case 'contact':
        return handleContactClick();
      default:
        return null;
    }
  });

  return (
    <Grid item component={Paper} className={classes.card}>
      <Grid container direction="column" alignItems="center">
        <Grid item className={classes.imageContainer}>
          <img alt="Oops!" width="400" src={HeaderImage} />
        </Grid>
        <Grid item container direction="column" justify="center" className={classes.titleContainer}>
          <Grid item>
            <Typography variant="h1" className={classes.title}>
              {t('Sorry!')}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h3" className={classes.errorMessage}>
              {t('Internal Server Error')}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1">
              {t(
                'The Page you are looking for doesn’t exist or another error occurred. Please go back or navigate to a different page.'
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid container justify="center">
          <Button
            variant="outlined"
            color="secondary"
            disableElevation
            startIcon={<HomeIcon />}
            onClick={handleClick('/')}
          >
            {t('Back to Home')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            startIcon={<UndoIcon />}
            className={classes.homeButton}
            onClick={handleClick(returnUrl as string)}
          >
            {t('Go Back')}
          </Button>
        </Grid>
        <Divider className={classes.dividerSpacing} />
        <Grid container justify="space-between" wrap="nowrap">
          <Grid item>
            <Typography variant="h3">{t('Still Having Trouble?')}</Typography>
            <Typography>
              {t('Send us a message or contact us at 301.263.2700 or 1.888.983.8409')}
            </Typography>
          </Grid>
          <Grid item className={classes.contactButtonContainer}>
            <Button
              variant="outlined"
              color="secondary"
              disableElevation
              startIcon={<ContactMailIcon />}
              className={classes.contactButton}
              onClick={handleContactClick}
              data-testid="contact-button"
            >
              {t('Contact Us')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ErrorPage;
